import { Action, PayloadAction, ThunkDispatch, createSlice } from '@reduxjs/toolkit';
import { AppDispatch, AppThunk, RootState } from '..';
import { AuthErrorKey, getAuthErrorKey } from 'utils/authErrors';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  User,
  createUserWithEmailAndPassword,
  getAuth,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword
} from 'firebase/auth';
import {
  PROFILE_NOT_FOUND, createNewUser, fetchUserDetails, resetUserState,
} from './userSlice';
import { deleteUserAPI, getUserAPI } from 'api/user';
import { requestPasswordResetEmail, requestVerificationEmail } from 'api/auth';
import { USER_ROLE } from 'interfaces/enum';
import moment from 'moment';

interface AuthState {
  user: SerializableUser | null;
  loading: boolean;
  error?: string;
  success?: string;
}

type Provider = 'google.com' | 'password' | null;

interface SerializableUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  providerId?: Provider;
}

const extractSerializableUserData = (user: User): SerializableUser => {
  return {
    uid: user.uid,
    email: user.email || '',
    emailVerified: user.emailVerified ||
      moment(user.metadata.creationTime, 'ddd, DD MMM YYYY HH:mm:ss [GMT]')
        .isBefore(moment('11 July 2023 10:16:44 +0300', 'DD MMMM YYYY HH:mm:ss Z')),
    providerId: user.providerData[0].providerId as Provider,
  };
};

const initialState: AuthState = {
  user: null,
  loading: true,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<SerializableUser | null>) => {
      if (action.payload) {
        // Create a new serializable object
        state.user = action.payload;
      } else {
        state.user = null;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setAuthError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setAuthSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = undefined;
      state.success = action.payload;
    }
  },
});

export const { setUser, setLoading, setAuthError, setAuthSuccess } = authSlice.actions;

const personalFields = (data: UserPersonalData) => ({
  countryCode: data.countryCode,
  city: data.city,
  age: data.age,
  gender: data.gender,
  languages: data.languages,
});

/** Result of a sign-in attempt, for the login form. */
export type SignInOutcome =
  | { kind: 'ok' }
  | { kind: 'profileMissing' }
  | { kind: 'profileUnavailable' } // backend down or unreachable: retry later
  | { kind: 'authError'; error: AuthErrorKey };

const outcomeFromFetch = (
  response: Awaited<ReturnType<ReturnType<typeof fetchUserDetails>>>,
): SignInOutcome => {
  if (fetchUserDetails.rejected.match(response)) {
    return { kind: 'profileUnavailable' };
  }
  if (response.payload === PROFILE_NOT_FOUND) {
    return { kind: 'profileMissing' };
  }
  return { kind: 'ok' };
};

export const googleSignIn = (): AppThunk<Promise<SignInOutcome>> =>
  async (dispatch) => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try {
      dispatch(setLoading(true));
      const result = await signInWithPopup(auth, provider);
      const response = await dispatch(fetchUserDetails(result.user.uid));
      return outcomeFromFetch(response);
    } catch (err) {
      return { kind: 'authError', error: getAuthErrorKey(err) };
    } finally {
      dispatch(setLoading(false));
    }
  };

export const signIn = (
  email: string,
  password: string,
): AppThunk<Promise<SignInOutcome>> =>
  async (dispatch) => {
    const auth = getAuth();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const response = await dispatch(fetchUserDetails(userCredential.user.uid));
      return outcomeFromFetch(response);
    } catch (err) {
      return { kind: 'authError', error: getAuthErrorKey(err) };
    }
  };

/** Result of a sign-up attempt, for the registration forms. */
export type SignUpOutcome =
  | { kind: 'ok' }
  /** Firebase account created, but the profile could not be saved: retry with retryProfile. */
  | { kind: 'profileNotSaved'; retryProfile: () => Promise<boolean> }
  | { kind: 'authError'; error: AuthErrorKey };

const buildCreateUserPayload = (
  userId: string,
  email: string,
  data: UserRegistrationByGoogle<UserPersonalData | CreateUserRecruiterPartData>,
): CreateUser<UserPersonalData | CreateUserRecruiterPartData> => {
  const commonFields = {
    userId,
    email,
    role: data.role,
    firstName: data.firstName,
    lastName: data.lastName,
  };
  if (data.role === USER_ROLE.RECRUITER) {
    const recruiter = data as UserRegistrationByGoogle<CreateUserRecruiterPartData>;
    return { ...commonFields, position: recruiter.position, companyInfo: recruiter.companyInfo };
  }
  return { ...commonFields, ...personalFields(data as UserRegistrationByGoogle<UserPersonalData>) };
};

/** Creates the backend profile for a freshly created Firebase account. */
type ThunkDispatchType = ThunkDispatch<RootState, unknown, Action<string>>;

const createProfileOutcome = async (
  dispatch: ThunkDispatchType,
  payload: CreateUser<UserPersonalData | CreateUserRecruiterPartData>,
): Promise<SignUpOutcome> => {
  const attempt = async () => !createNewUser.rejected.match(await dispatch(createNewUser(payload)));
  if (await attempt()) {
    return { kind: 'ok' };
  }
  return { kind: 'profileNotSaved', retryProfile: attempt };
};

export const googleSignUp = (
  data: UserRegistrationByGoogle<UserPersonalData | CreateUserRecruiterPartData>,
): AppThunk<Promise<SignUpOutcome>> =>
  async (dispatch) => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try {
      dispatch(setLoading(true));
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const existing = await getUserAPI(user.uid);
      if (typeof existing !== 'number') {
        // This Google account already has a profile: it must log in, not sign up.
        dispatch(logout());
        return { kind: 'authError', error: 'emailInUse' };
      }
      const payload = buildCreateUserPayload(user.uid, user.email || '', data);
      if (existing !== PROFILE_NOT_FOUND) {
        // Backend unavailable: keep the Firebase session so the profile can be saved on retry.
        return {
          kind: 'profileNotSaved',
          retryProfile: async () => (await createProfileOutcome(dispatch, payload)).kind === 'ok',
        };
      }
      return await createProfileOutcome(dispatch, payload);
    } catch (err) {
      return { kind: 'authError', error: getAuthErrorKey(err) };
    } finally {
      dispatch(setLoading(false));
    }
  };

export const signUp = (
  data: UserRegistrationByEmail<UserPersonalData | CreateUserRecruiterPartData>,
): AppThunk<Promise<SignUpOutcome>> => async (dispatch) => {
  const auth = getAuth();
  try {
    dispatch(setLoading(true));
    const { user } = await createUserWithEmailAndPassword(auth, data.email, data.password);
    // A brand-new Firebase account cannot have a profile; a failure here is the backend's.
    return await createProfileOutcome(
      dispatch, buildCreateUserPayload(user.uid, user.email || '', data));
  } catch (err) {
    // Firebase refused to create the account (email in use, weak password, network...).
    return { kind: 'authError', error: getAuthErrorKey(err) };
  } finally {
    dispatch(setLoading(false));
  }
};

export const logout = (): AppThunk => async (dispatch) => {
  const auth = getAuth();
  try {
    await signOut(auth);
    dispatch(resetUserState());

  } catch (error) {
    // eslint-disable-next-line 
    console.error(error);
  }
};

/**
 * Where a successful "Try again" on a saved profile leads: straight to the dashboard when the
 * address is already verified (Google accounts, or verified meanwhile), else the verification
 * screen, which the route guards would impose anyway.
 */
export const isCurrentUserVerified = (): boolean => !!getAuth().currentUser?.emailVerified;

/** Wait shown when nobody said how long (the backend allows one email per minute). */
const FALLBACK_RETRY_AFTER_SEC = 60;

/** Result of a verification email request, for the verification screen. */
export type VerificationOutcome =
  | { kind: 'sent' }
  | { kind: 'alreadyVerified' }
  | { kind: 'tooMany'; retryAfterSec: number }
  | { kind: 'sessionExpired' }
  | { kind: 'error' };

/**
 * Reloads the Firebase user and refreshes its ID token. Returns true when the address is now
 * verified, after updating the store; onIdTokenChanged listeners (useAuthUserStatus) see it too.
 * The token refresh also matters for the backend, which reads email_verified from the token.
 */
export const refreshEmailVerified = (): AppThunk<Promise<boolean>> => async (dispatch) => {
  const user = getAuth().currentUser;
  if (!user) return false;
  try {
    await user.reload();
  } catch {
    return false;
  }
  if (!user.emailVerified) return false;
  try {
    await user.getIdToken(true);
  } catch {
    // the store update below is enough for the pages; the guard catches up on the next token
  }
  dispatch(setUser(extractSerializableUserData(user)));
  return true;
};

export const verifyEmail = (
  locale = 'en',
): AppThunk<Promise<VerificationOutcome>> => async (dispatch) => {
  const user = getAuth().currentUser;
  if (!user) {
    return { kind: 'sessionExpired' };
  }
  // Clicked the link in another tab, or came back from it: nothing to send.
  if (await dispatch(refreshEmailVerified())) {
    return { kind: 'alreadyVerified' };
  }
  // Sent by the backend first (Brevo); Firebase's own email as a fallback, also when the
  // backend refuses the session (401): the user is signed in here, so Firebase can still send.
  const { outcome, retryAfterSec } = await requestVerificationEmail(locale);
  switch (outcome) {
  case 'sent':
  case 'alreadyVerified':
    return { kind: outcome };
  case 'tooMany':
    return { kind: 'tooMany', retryAfterSec: retryAfterSec ?? FALLBACK_RETRY_AFTER_SEC };
  default:
    break;
  }
  try {
    await sendEmailVerification(user);
    return { kind: 'sent' };
  } catch (err) {
    if (getAuthErrorKey(err) === 'tooManyRequests') {
      return { kind: 'tooMany', retryAfterSec: FALLBACK_RETRY_AFTER_SEC };
    }
    return { kind: 'error' };
  }
};

// Send a password reset email (forgot)
export type ResetPasswordOutcome = 'sent' | 'tooMany' | 'invalidEmail' | 'error';

export const resetPassword = (
  email: string,
  locale = 'en',
): AppThunk<Promise<ResetPasswordOutcome>> => async () => {
  // Sent by the backend first (Brevo, logged); Firebase's own email only as a fallback.
  const { outcome } = await requestPasswordResetEmail(email, locale);
  if (outcome === 'sent' || outcome === 'tooMany') {
    return outcome;
  }
  try {
    await sendPasswordResetEmail(getAuth(), email.trim());
    return 'sent';
  } catch (err) {
    const code = (err as { code?: string })?.code ?? '';
    if (code === 'auth/user-not-found') {
      return 'sent'; // same answer as for an existing account
    }
    if (code === 'auth/too-many-requests') {
      return 'tooMany';
    }
    if (code === 'auth/invalid-email') {
      return 'invalidEmail';
    }
    return 'error';
  }
};

// Change user password (settings)
export const changePassword = (
  password: string,
  newPassword: string,
  errors: { wrongPassword: string, loginBeforeChangePassword: string },
  succesfull: { passwordChanged: string }
):
  AppThunk => async (dispatch, getState) => {
  const authState = getState().auth.user;
  const auth = getAuth();
  const user = auth.currentUser;

  const error = () => {
    dispatch(setAuthError(errors.wrongPassword));
  };

  if (user) {
    try {
      await dispatch(reauthenticateUser(authState, error, password)); // Await reauthentication
      await updatePassword(user, newPassword);
      return dispatch(setAuthSuccess(succesfull.passwordChanged));
    } catch (err) {
      return dispatch(setAuthError(errors.loginBeforeChangePassword));
    }
  }
};

export const reauthenticateUser = (
  authState: SerializableUser | null,
  error: () => void,
  password?: string,
): AppThunk<Promise<void>> => () => {
  return new Promise<void>((resolve, reject) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const providerId = authState?.providerId;
      let credential;
      if (providerId === 'password') {
        if (password) {
          credential = EmailAuthProvider.credential(authState?.email as string, password);
        } else {
          error();
          reject(new Error('No password provided'));
        }
      } else if (providerId === 'google.com') {
        resolve();
      } else {
        error();
        reject(new Error('Invalid providerId'));
      }
      if (credential) {
        reauthenticateWithCredential(user, credential)
          .then(() => resolve())
          .catch(reject);
      }
    } else {
      error();
      reject(new Error('No user'));
    }
  });
};



export const removeUser = (
  error: () => void,
  password?: string,
): AppThunk => async (dispatch, getState) => {
  const authState = getState().auth.user;
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    try {
      await dispatch(reauthenticateUser(authState, error, password));
      const response = await deleteUserAPI();

      if (response.status === 204) {
        dispatch(resetUserState());
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (err) {
      error();
    }
  } else {
    error();
  }
};

export const authStateListener = () => {
  return (dispatch: AppDispatch) => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      dispatch(setLoading(true));
      if (user) {
        const serializableUser = extractSerializableUserData(user);
        dispatch(setUser(serializableUser));
      } else {
        dispatch(setUser(null));
      }
      dispatch(setLoading(false));
    });

    // Return the unsubscribe function to be able to clean up the listener
    return unsubscribe;
  };
};


export default authSlice.reducer;
